if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const sections = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : null;
        if (this.cart) this.cart.setActiveElement(document.activeElement);

        // Priced customizer add-ons (Light Base, Keychain, Gift Bag, etc.) are
        // submitted as line-item properties, which Shopify never uses for
        // pricing. Any add-on with a configured variant ID gets added as its
        // own real line item alongside the base item so its price actually
        // hits the cart total; add-ons without one keep the old (price-less)
        // behavior.
        const groupId =
          window.crypto && crypto.randomUUID ? crypto.randomUUID() : `pg_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        // Short, visible tag shown on the base item AND every add-on line so
        // a customer can tell them apart when the same product is added to
        // the cart more than once with different customization — the
        // product title alone doesn't disambiguate that case.
        const refCode = groupId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase();
        const addonItems = this.getPricedAddonItems(formData, groupId, refCode);

        this.savePreviewThumb(groupId);

        if (addonItems.length) {
          const baseProperties = this.getPropertiesObject(formData);
          baseProperties['_Personalization Group'] = groupId;
          baseProperties['_Personalization Ref'] = refCode;
          const baseItem = {
            id: formData.get('id'),
            quantity: parseInt(formData.get('quantity'), 10) || 1,
            properties: baseProperties,
          };
          const body = { items: [baseItem, ...addonItems] };
          if (sections) {
            body.sections = sections;
            body.sections_url = window.location.pathname;
          }
          config.body = JSON.stringify(body);
        } else {
          delete config.headers['Content-Type'];
          if (sections) {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (Array.isArray(response.items) && !response.key) {
              // Multi-item add responds with { items: [...] } instead of the
              // single added line item — backfill .key/.id from the base item
              // so cart-notification/cart-drawer render as they did before.
              const [firstItem] = response.items;
              if (firstItem) {
                response.key = firstItem.key;
                response.id = firstItem.id;
              }
            }
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (this.form.dataset.redirectToCheckout === 'true') {
              window.location = '/checkout';
              return;
            } else if (this.form.dataset.redirectToCart === 'true' || !this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            if (this.error) {
              delete this.form.dataset.redirectToCheckout;
              delete this.form.dataset.redirectToCart;
            }
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      getPropertiesObject(formData) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) properties[match[1]] = value;
        }
        return properties;
      }

      // The uploaded preview on S3 is private, so the cart can't load it.
      // Keep a small copy of the on-screen preview in this browser instead;
      // the cart page swaps it in for the stock product photo (same device only).
      savePreviewThumb(groupId) {
        try {
          const source = document.querySelector('[data-crystal-canvas]');
          if (!source || !source.width || !window.CrystalPreview || !window.CrystalPreview.hasPhoto()) return;
          const width = 300;
          const thumb = document.createElement('canvas');
          thumb.width = width;
          thumb.height = Math.round((source.height / source.width) * width);
          const ctx = thumb.getContext('2d');
          ctx.fillStyle = '#1a1410';
          ctx.fillRect(0, 0, thumb.width, thumb.height);
          ctx.drawImage(source, 0, 0, thumb.width, thumb.height);
          localStorage.setItem(`rm-preview:${groupId}`, thumb.toDataURL('image/jpeg', 0.8));
        } catch (e) {
          // Storage full or blocked — the cart just shows the product photo.
        }
      }

      getPricedAddonItems(formData, groupId, refCode) {
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        // A form containing an <input name="id"> (as this one does, for the
        // variant ID) shadows the form element's own .id property in every
        // browser, so it must be read via getAttribute instead.
        const formId = this.form.getAttribute('id');
        const candidates = document.querySelectorAll('[data-addon-price][data-addon-variant-id]:checked');
        const items = [];

        candidates.forEach((input) => {
          if (input.getAttribute('form') !== formId) return;
          const price = parseFloat(input.dataset.addonPrice || '0');
          const variantId = (input.dataset.addonVariantId || '').trim();
          if (price <= 0 || !variantId) return;

          // e.g. name="properties[Light Base]" -> "Light Base" — matches the
          // property name used on the base item, so cart.js's removal-sync
          // knows which one to reset when this add-on line is deleted.
          const match = input.name.match(/^properties\[(.+)\]$/);
          const optionName = match ? match[1] : '';

          const properties = {
            '_Personalization Group': groupId,
            '_Personalization Option': optionName,
          };
          // Hidden (leading underscore) so checkout and emails show just the
          // item name; the ERP still reads both to tie the add-on to its crystal.
          const productTitle = this.dataset.productTitle;
          if (productTitle) properties['_Personalized for'] = productTitle;
          properties['_Personalization Ref'] = refCode;

          items.push({
            id: variantId,
            quantity,
            properties,
          });
        });

        return items;
      }
    }
  );
}
